import type { ConsumerApiClient } from '../client';

export interface CartItem {
  id: string;
  productType: string;
  productName: string;
  quantity?: number;
  unitPriceCents: number;
  totalPriceCents: number;
  size?: string;
  finish?: string;
  thumbnailUrl?: string;
  content?: string;
  templateId?: string;
  mediaAssetIds?: string[];
}

export interface Cart {
  id: string;
  userId: string;
  status: string;
  facilityId: string | null;
  recipientId: string | null;
  contactId?: string | null;
  rulesAcknowledgedAt: string | null;
  promoCode: string | null;
  subtotalCents: number;
  taxCents: number;
  shippingCents: number;
  shippingSpeed: string | null;
  discountCents: number;
  totalCents: number;
  items: CartItem[];
  facility: unknown | null;
  recipient: unknown | null;
  da_eligible?: boolean;
  da_hidden_reason?: string | null;
  da_price?: number;
}

export interface AddToCartRequest {
  productType: string;
  size?: string;
  finish?: string;
  mediaUrls?: string[];
  mediaAssetIds?: string[];
  content?: string;
  pageMetadata?: { isColor: boolean }[];
}

export interface CartSummary {
  itemCount: number;
  productType: string | null;
  size?: string | null;
  finish?: string | null;
  totalCents: number;
}

export interface ShippingOption {
  id: string;
  label: string;
  description: string;
  priceCents: number;
  estimatedDays: string;
}

export interface ShippingOptionsResponse {
  options: ShippingOption[];
  selectedId: string | null;
}

export interface CartApi {
  getCart(): Promise<Cart>;
  getCartSummary(): Promise<CartSummary>;
  clearCart(): Promise<Cart>;
  setRecipient(recipientId: string, facilityId: string): Promise<Cart>;
  getShippingOptions(): Promise<ShippingOptionsResponse>;
  setShipping(shippingSpeed: string): Promise<Cart>;
  acknowledgeRules(): Promise<Cart>;
  addItem(data: AddToCartRequest): Promise<Cart>;
  removeItem(itemId: string): Promise<Cart>;
  applyPromo(code: string): Promise<Cart>;
  removePromo(): Promise<Cart>;
  checkout(): Promise<{ orderId: string; orderNumber: string }>;
}

export function createCartApi(client: ConsumerApiClient): CartApi {
  return {
    getCart() {
      return client.get('/cart');
    },
    getCartSummary() {
      return client.get('/cart/summary');
    },
    clearCart() {
      return client.delete('/cart/clear');
    },
    setRecipient(recipientId, facilityId) {
      return client.put('/cart/recipient', { recipientId, facilityId });
    },
    getShippingOptions() {
      return client.get('/cart/shipping-options');
    },
    setShipping(shippingSpeed) {
      return client.put('/cart/shipping', { shippingSpeed });
    },
    acknowledgeRules() {
      return client.post('/cart/acknowledge-rules');
    },
    addItem(data) {
      return client.post('/cart/items', data);
    },
    removeItem(itemId) {
      return client.delete(`/cart/items/${itemId}`);
    },
    applyPromo(code) {
      return client.post('/cart/apply-promo', { code });
    },
    removePromo() {
      return client.delete('/cart/promo');
    },
    async checkout() {
      const data = await client.post<{ id?: string; orderId?: string; orderNumber: string }>('/cart/checkout');
      return {
        orderId: data.id || data.orderId || '',
        orderNumber: data.orderNumber,
      };
    },
  };
}
